<script lang="ts">
import { useCountdown } from '@/plugins/useCountdown'
import type { InstantWinDraw, ReservationData } from '@/types'
import { DateTime } from 'luxon'
import { type PropType, defineComponent } from 'vue'

/**
 * InstantWinCountdownBanner
 */
export default defineComponent({
  props: {
    instantWin: {
      type: Object as PropType<InstantWinDraw>,
      required: true,
    },
    reservationData: {
      type: Object as PropType<ReservationData>,
      required: true,
    },
    emitOnExpiry: {
      type: Boolean,
      default: false,
    },
    expiryRouteName: {
      type: String,
      default: 'instant-win',
    },
    bannerClass: {
      type: String,
      default: 'sticky top-[65px] z-10 !p-0',
    },
    countdownClass: {
      type: String,
      default: '',
    },
  },
  emits: ['countdownFinished'],
  data() {
    return {
      ticketExpirationCountdown: useCountdown(() => {
        if (this.emitOnExpiry) {
          this.$emit('countdownFinished')
        } else {
          void this.$router.push({
            name: this.expiryRouteName,
            params: {
              instantWinCode: this.instantWin.code,
            },
          })
        }
      }),
    }
  },
  beforeMount() {
    this.ticketExpirationCountdown.start(
      this.createTicketExpirationDatetime(this.reservationData.expires_at)
    )
  },
  methods: {
    createTicketExpirationDatetime(expiry: string): DateTime {
      return DateTime.fromISO(expiry)
    },
  },
})
</script>

<template>
  <AppBanner labelled-by="instant-win-draw-banner" :class="bannerClass">
    <div
      class="flex w-full flex-col items-center justify-center gap-x-2 bg-primary px-5 py-3 text-pure-white"
      :class="countdownClass"
    >
      <p class="whitespace-nowrap font-bold">
        Your reserved tickets will expire in:
      </p>
      <time
        v-if="reservationData"
        :datetime="
          createTicketExpirationDatetime(reservationData.expires_at).toString()
        "
        class="flex gap-1"
      >
        <span>
          <strong>{{
            ticketExpirationCountdown.remainingDateTimeFormatted.minutes
          }}</strong>
          {{
            $t(
              'countdown.min',
              ticketExpirationCountdown.remainingDateTime.minutes
            )
          }}
        </span>
        <span>
          <strong>{{
            ticketExpirationCountdown.remainingDateTimeFormatted.seconds
          }}</strong>
          {{
            $t(
              'countdown.sec',
              ticketExpirationCountdown.remainingDateTime.seconds
            )
          }}
        </span>
      </time>
    </div>
  </AppBanner>
</template>

<style lang="scss" scoped></style>
